import React, { useState } from "react";
import { SubscriptionClient } from "../sdk";
import { useSubscription } from "../context/SubscriptionContext";
import { TabLayoutHeaderToolBar } from "./smartlayout/fragments";
import PendingOrders from "./PendingOrder";
import BucketPositions from "./Position";
import BucketCollections from "./BucketCollection";
import "./smartlayout/layout.css";
import "./TabSection.css";

export type TabSectionTabId = "orders" | "positions" | "buckets";

export interface TabSectionProps {
  baseURL: string;
  /** Optional: when inside SubscriptionProvider, children use context. */
  subscriptionClient?: SubscriptionClient;
  authToken?: string;
  walletAddress: string;
  mnemonic?: string;
  height?: string;
  refreshInterval?: number;
  /** Passed to Positions and used when view is "by bucket". */
  selectedBucketId?: string | null;
  /** Called when a bucket is selected in Bucket Collections (e.g. to sync with Positions). */
  onBucketSelect?: (bucketId: string, totalEquity: number) => void;
  /** Called when Transfer button is clicked in Bucket Collections (opens transfer modal at page level). */
  onRequestOpenTransferModal?: () => void;
  /** Called when Send to Address button is clicked (opens wallet transfer modal). */
  onRequestOpenWalletTransferModal?: () => void;
  /** Optional controlled active tab; when provided with onTabChange, uses controlled mode */
  activeTab?: TabSectionTabId;
  /** Called when tab changes; use with activeTab for controlled mode */
  onTabChange?: (tabId: TabSectionTabId) => void;
}

const TAB_LABELS: Record<TabSectionTabId, string> = {
  orders: "Orders",
  positions: "Positions",
  buckets: "Buckets",
};

function TabSection({
  baseURL,
  subscriptionClient: subscriptionClientProp,
  authToken,
  walletAddress,
  mnemonic,
  height = "400px",
  refreshInterval,
  selectedBucketId = null,
  onBucketSelect,
  onRequestOpenTransferModal,
  onRequestOpenWalletTransferModal,
  activeTab: controlledTab,
  onTabChange,
}: TabSectionProps): JSX.Element {
  const { client: contextClient } = useSubscription();
  const subscriptionClient = subscriptionClientProp ?? contextClient;

  const [internalTab, setInternalTab] = useState<TabSectionTabId>("orders");
  const isControlled = controlledTab != null && onTabChange != null;
  const activeTab = isControlled ? controlledTab : internalTab;
  const setActiveTab = isControlled
    ? onTabChange
    : setInternalTab;
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);

  const tabs = (Object.keys(TAB_LABELS) as TabSectionTabId[]).map((id) => ({
    id,
    label: TAB_LABELS[id],
  }));

  return (
    <div className="layout-container tab-section-root" style={{ height }}>
      <TabLayoutHeaderToolBar
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={(id) => setActiveTab(id as TabSectionTabId)}
      >
        {headerActions}
      </TabLayoutHeaderToolBar>
      <div className="tab-section-content">
        {activeTab === "orders" && (
          <PendingOrders
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            authToken={authToken}
            walletAddress={walletAddress}
            mnemonic={mnemonic}
            height="100%"
            refreshInterval={refreshInterval}
            hide_header
            onRequestHeaderActions={setHeaderActions}
          />
        )}
        {activeTab === "positions" && (
          <BucketPositions
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            authToken={authToken}
            walletAddress={walletAddress}
            mnemonic={mnemonic}
            height="100%"
            refreshInterval={refreshInterval}
            selectedBucketId={selectedBucketId}
            hide_header
            onRequestHeaderActions={setHeaderActions}
          />
        )}
        {activeTab === "buckets" && (
          <BucketCollections
            baseURL={baseURL}
            subscriptionClient={subscriptionClient}
            walletAddress={walletAddress}
            mnemonic={mnemonic ?? ""}
            authToken={authToken}
            height="100%"
            refreshInterval={refreshInterval}
            onBucketSelect={onBucketSelect}
            onRequestOpenTransferModal={onRequestOpenTransferModal}
            onRequestOpenWalletTransferModal={onRequestOpenWalletTransferModal}
            hide_header
            onRequestHeaderActions={setHeaderActions}
          />
        )}
      </div>
    </div>
  );
}

export default React.memo(TabSection);
